<script lang="ts">
  import type { StoresKey } from "~/stores";
  import { getStoresContext } from "~/stores";
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';

  export let request_store_key: StoresKey;
  export let openingHours: any[] = [];
  export let todayHours: any = null;
  export let exceptionalHours: any[] = [];
  export let dayTranslations: {
    today: string;
    closed: string;
    byAppointment: string;
    monFri: string;
    sat: string;
    sun: string;
    noOpeningHours: string;
    exceptionalHours: string;
    weekdays: string[];
  };
  
  const { locale } = getStoresContext(request_store_key);
  let isReady = false;
  
  onMount(() => {
    // Add a small delay to ensure translation service is ready
    setTimeout(() => {
      isReady = true;
    }, 300);
  });

  function formatHours(hours: any) {
    if (!hours) return '';
    
    // Handle different opening statuses
    if (hours.openingStatus === 'CLOSED') {
      return dayTranslations.closed;
    } else if (hours.openingStatus === 'BY_APPOINTMENT') {
      return dayTranslations.byAppointment;
    }
    
    const timeRange = hours.dailyOpeningHours?.[0];
    if (!timeRange) return '';
    return `${timeRange.from} - ${timeRange.to}`;
  }

  function formatExceptionalHours(hours: any) {
    if (!hours) return '';
    
    if (!hours.open) {
      return dayTranslations.closed;
    }
    
    if (hours.byAppointment) {
      return dayTranslations.byAppointment;
    }
    
    const fromTime = `${String(hours.fromHour).padStart(2, '0')}:${String(hours.fromMinute).padStart(2, '0')}`;
    const toTime = `${String(hours.toHour).padStart(2, '0')}:${String(hours.toMinute).padStart(2, '0')}`;
    
    return `${fromTime} - ${toTime}`;
  }

  function formatDate(dateString: string) {
    const dateObj = new Date(dateString);
    const dateOptions: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'numeric',
      year: 'numeric',
    };
    return dateObj.toLocaleDateString($locale, dateOptions).replace(/\//g, '.');
  }

  function getWeekdayLabel(days: string): string {
    // Special cases for common day combinations
    if (days === '1,2,3,4,5') {
      return dayTranslations.monFri;
    } else if (days === '6') {
      return dayTranslations.sat;
    } else if (days === '0') {
      return dayTranslations.sun;
    } else if (days.includes(',')) {
      // For custom day combinations, format each day individually
      const dayNumbers = days.split(',').map(d => parseInt(d.trim()));
      const weekdayNames = dayNumbers.map(dayNum => {
        // Handle bounds checking
        if (dayNum >= 0 && dayNum < 7) {
          return dayTranslations.weekdays[dayNum];
        }
        return '';
      });
      return weekdayNames.join(', ');
    } else {
      // For a single day
      const dayNum = parseInt(days);
      // Handle bounds checking
      if (dayNum >= 0 && dayNum < 7) {
        return dayTranslations.weekdays[dayNum];
      }
      return '';
    }
  }
</script>

<div class="opening-hours">
  {#if isReady}
    {#if openingHours && openingHours.length > 0}
      <div class="hours-grid" transition:fade={{ duration: 300 }}>
        <div class="hours-section">
          <div class="hours-row">
            <span class="day today">{dayTranslations.today}</span>
            <span class="dots"></span>
            <span class="time">{formatHours(todayHours)}</span>
          </div>
          {#each openingHours || [] as hours}
            <div class="hours-row">
              <span class="day">
                {getWeekdayLabel(hours.days)}
              </span>
              <span class="dots"></span>
              <span class="time">{formatHours(hours)}</span>
            </div>
          {/each}
        </div>
      </div>
    {:else}
      <div class="no-hours-message" transition:fade={{ duration: 300 }}>
        {dayTranslations.noOpeningHours}
      </div>
    {/if}

    {#if exceptionalHours && exceptionalHours.length > 0}
      <div transition:fade={{ duration: 300, delay: 100 }}>
        <h2 class="exceptional-hours-title">{dayTranslations.exceptionalHours}</h2>
        <div class="hours-grid">
          <div class="hours-section">
            {#each exceptionalHours as hours}
              <div class="hours-row">
                <span class="day">
                  {formatDate(hours.date)}
                  {#if hours.reason} - {hours.reason}{/if}
                </span>
                <span class="dots"></span>
                <span class="time">{formatExceptionalHours(hours)}</span>
              </div>
            {/each}
          </div>
        </div>
      </div>
    {/if}
  {:else}
    <!-- Loading placeholder -->
    <div class="loading-placeholder">
      <div class="placeholder-line"></div>
      <div class="placeholder-line"></div>
      <div class="placeholder-line"></div>
    </div>
  {/if}
</div>

<style lang="scss">
  .exceptional-hours-title {
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    font-family: inherit;
  }

  .hours-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 48px;

    @media (max-width: 475px) {
      grid-template-columns: 1fr !important;
      gap: 32px;
    }
  }

  .hours-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: baseline;
    margin-bottom: 20px;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }

    .day {
      &.today {
        font-weight: 700;
      }
    }

    .dots {
      margin: 0 8px;
      border-bottom: 1px dotted #999;
      height: 1px;
      align-self: end;
      margin-bottom: 4px;
    }

    .time {
      font-weight: 400;
    }
  }

  .no-hours-message {
    font-family: inherit;
    font-size: inherit;
    margin-bottom: 1rem;
    font-style: italic;
  }
  
  .loading-placeholder {
    padding: 1rem 0;
    
    .placeholder-line {
      height: 20px;
      background: #f0f0f0;
      border-radius: 4px;
      margin-bottom: 15px;
      
      &:nth-child(1) {
        width: 100%;
      }
      
      &:nth-child(2) {
        width: 85%;
      }
      
      &:nth-child(3) {
        width: 75%;
      }
    }
  }
</style> 